import {
  AdditiveBlending,
  BackSide,
  Box3,
  BufferAttribute,
  BufferGeometry,
  Color,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Points,
  PointsMaterial,
  Vector3,
  type Object3D,
} from 'three';
import { addRim } from './materials';

/** Aktualizacja efektu co klatkę (np. iskry). */
export type FxUpdater = (time: number) => void;

/**
 * Wizualny progres rzadkości na samym przedmiocie:
 * 0 zwykły — bez zmian, 1 rzadki — kolorowy połysk, 2 epicki — świecąca obwódka,
 * 3+ legendarny — mocna obwódka i iskry wokół przedmiotu.
 * Działa na pojedynczej siatce i na całym modelu (wiele siatek).
 */
export function decorateItem(root: Object3D, color: string, tier: number): FxUpdater | null {
  if (tier <= 0) return null;
  const c = new Color(color);
  const meshes: Mesh[] = [];
  root.traverse((o) => {
    if ((o as Mesh).isMesh) meshes.push(o as Mesh);
  });

  for (const mesh of meshes) {
    const base = mesh.material as MeshStandardMaterial;
    if (base.isMeshStandardMaterial) {
      const mat = base.clone();
      mat.emissive = c.clone();
      mat.emissiveIntensity = [0, 0.05, 0.12, 0.22][Math.min(tier, 3)]!;
      addRim(mat);
      mesh.material = mat;
    }
    if (tier >= 2) mesh.add(glowShell(mesh, c, tier));
  }

  return tier >= 3 ? addSparkles(root, c) : null;
}

function glowShell(mesh: Mesh, color: Color, tier: number): Mesh {
  // Grubość obwódki liczona w jednostkach sceny, więc dzielimy przez skalę świata siatki.
  mesh.updateWorldMatrix(true, false);
  const s = mesh.getWorldScale(new Vector3()).x || 1;
  const shell = new Mesh(
    inflate(mesh.geometry, (tier >= 3 ? 0.03 : 0.02) / s),
    new MeshBasicMaterial({
      color,
      transparent: true,
      opacity: tier >= 3 ? 0.32 : 0.2,
      blending: AdditiveBlending,
      side: BackSide,
      depthWrite: false,
      toneMapped: false,
    }),
  );
  shell.castShadow = false;
  shell.renderOrder = 2;
  return shell;
}

/** Kopia geometrii „napompowana” wzdłuż normalnych — tania poświata. */
function inflate(geometry: BufferGeometry, amount: number): BufferGeometry {
  const g = geometry.clone();
  if (!g.attributes.normal) g.computeVertexNormals();
  const pos = g.attributes.position as BufferAttribute;
  const nor = g.attributes.normal as BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    pos.setXYZ(i, pos.getX(i) + nor.getX(i) * amount, pos.getY(i) + nor.getY(i) * amount, pos.getZ(i) + nor.getZ(i) * amount);
  }
  pos.needsUpdate = true;
  return g;
}

/** Obrys modelu w jego własnym układzie (bez transformacji samego korzenia). */
function localBox(root: Object3D): Box3 {
  const saved = { p: root.position.clone(), q: root.quaternion.clone(), s: root.scale.clone(), parent: root.parent };
  root.removeFromParent();
  root.position.set(0, 0, 0);
  root.quaternion.identity();
  root.scale.set(1, 1, 1);
  root.updateMatrixWorld(true);
  const box = new Box3().setFromObject(root);
  root.position.copy(saved.p);
  root.quaternion.copy(saved.q);
  root.scale.copy(saved.s);
  saved.parent?.add(root);
  root.updateMatrixWorld(true);
  return box;
}

function addSparkles(root: Object3D, color: Color): FxUpdater {
  const box = localBox(root);
  const count = 18;
  const seeds = Array.from({ length: count }, (_, i) => ({
    x: box.min.x + (((i * 7919) % 97) / 97) * (box.max.x - box.min.x),
    y: box.min.y + (((i * 104729) % 89) / 89) * (box.max.y - box.min.y),
    z: box.min.z + (((i * 1299709) % 83) / 83) * (box.max.z - box.min.z),
    phase: (i * 0.618) % 1,
  }));
  const positions = new Float32Array(count * 3);
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  const points = new Points(
    geometry,
    new PointsMaterial({ color, size: 0.09, transparent: true, opacity: 0.9, blending: AdditiveBlending, depthWrite: false, toneMapped: false }),
  );
  points.frustumCulled = false;
  root.add(points);

  return (time) => {
    seeds.forEach((s, i) => {
      const t = (time * 0.6 + s.phase) % 1;
      positions[i * 3] = s.x + Math.sin(time * 2 + i) * 0.05;
      positions[i * 3 + 1] = s.y + t * 0.35;
      positions[i * 3 + 2] = s.z + Math.cos(time * 2 + i) * 0.05;
    });
    geometry.attributes.position!.needsUpdate = true;
    (points.material as PointsMaterial).opacity = 0.5 + 0.4 * Math.sin(time * 6);
  };
}
